/**
 * Markets / i18n : fetch les markets disponibles via Shopify Storefront
 * `localization` query (pas de scope Admin requis, juste le public token).
 *
 * Cache mémoire 1h. Les markets changent rarement.
 */

import { storefront } from "../storefront/client.js";
import { LOCALIZATION } from "../storefront/queries.js";

export interface MarketEntry {
  countryCode: string;       // "FR", "US", "GB", ...
  countryName: string;       // "France", "United States", ...
  currencyCode: string;      // "EUR", "USD", ...
  currencySymbol: string;    // "€", "$", ...
  languages: Array<{
    isoCode: string;         // "FR", "EN", ...
    name: string;            // "French (France)", ...
    endonymName: string;     // "Français", "English", ...
  }>;
  unitSystem: string;
}

export interface MarketsState {
  current: { countryCode: string; languageCode: string };
  countries: MarketEntry[];
  fetchedAt: number;
}

interface LocalizationResponse {
  localization: {
    country: { isoCode: string; name: string; currency: { isoCode: string; symbol: string } };
    language: { isoCode: string; name: string; endonymName: string };
    availableCountries: Array<{
      isoCode: string;
      name: string;
      unitSystem: string;
      currency: { isoCode: string; symbol: string };
      availableLanguages: Array<{ isoCode: string; name: string; endonymName: string }>;
    }>;
  };
}

let cache: MarketsState | null = null;
const CACHE_TTL = 60 * 60 * 1000;

export async function fetchMarkets(): Promise<MarketsState> {
  if (cache && Date.now() - cache.fetchedAt < CACHE_TTL) return cache;

  try {
    const data = await storefront.query<LocalizationResponse>(LOCALIZATION, {});
    const loc = data.localization;
    const countries: MarketEntry[] = loc.availableCountries.map((c) => ({
      countryCode: c.isoCode,
      countryName: c.name,
      currencyCode: c.currency.isoCode,
      currencySymbol: c.currency.symbol,
      languages: c.availableLanguages,
      unitSystem: c.unitSystem,
    }));

    cache = {
      current: { countryCode: loc.country.isoCode, languageCode: loc.language.isoCode },
      countries,
      fetchedAt: Date.now(),
    };
    return cache;
  } catch (err) {
    if (cache) return cache;
    throw err;
  }
}

export function invalidateMarketsCache(): void {
  cache = null;
}
